import { useCallback, useEffect, useRef, useState } from 'react';
import { clock } from '../lib/reading';
import { cueIndexAt, lastCueBefore, type Cue } from '../lib/subtitles';
import { loadYouTubeApi } from '../lib/youtube';
import TranslatableText from './TranslatableText';

// ─────────────────────────────────────────────────────────────────────────────
// A YouTube embed with OUR controls and OUR subtitles.
//
// ── SUBTITLES ARE ONLY EVER IN THE LANGUAGE OF THE VIDEO ────────────────────
// YouTube's own captions are switched off and cannot be switched back on:
//   • controls: 0 removes YouTube's control bar — and with it the CC button and
//     the settings menu where "Auto-translate → English" lives;
//   • disablekb: 1 removes the "c" keyboard shortcut for captions;
//   • cc_load_policy: 0, and the captions module is UNLOADED whenever the
//     player reports an API change or starts playing — so a viewer whose
//     YouTube account says "always show captions" gets none either.
// What students see instead is the teacher's transcript (lib/subtitles.ts),
// shown under the video and tappable/draggable to translate, like stories.
//
// ── WATCH TRACKING ──────────────────────────────────────────────────────────
// Once a second, while playing, the playhead is compared with the last sample.
// An advance of up to 2.5s × playback rate is watching; more is a seek and is
// not counted. Slower playback counts at real time played; faster than 1× is
// not counted at all (Josiah, 2026-10-23), and the speed menu stops at 1×.
// ─────────────────────────────────────────────────────────────────────────────

type Props = {
  videoId: string;
  /** Seconds of the video played since the last call, plus its length. */
  onWatched: (seconds: number, duration: number) => void;
  onPlayingChange: (playing: boolean) => void;
  /** The teacher previewing it gets told how to fix it, not to ask the teacher. */
  forTeacher?: boolean;
  /** The owner blocks playback outside YouTube (error 101/150). */
  onBlocked?: () => void;
  /** The teacher's transcript, in the video's language. */
  cues?: Cue[] | null;
  source: string;
  target: string;
  onLookup?: (text: string, translation: string) => void;
  onSave?: (term: string, translation: string) => Promise<void>;
  savedKeys?: Set<string>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type YTPlayer = any;

// Never faster than normal: a watch has to be at real speed (Josiah, 2026-10-23), so time above 1× earns nothing.
const SPEEDS = [0.5, 0.75, 1];

export default function YouTubePlayer({
  videoId, onWatched, onPlayingChange, forTeacher, cues, source, target, onLookup, onSave, savedKeys, onBlocked,
}: Props) {
  const frameRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YTPlayer>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  // The first play must be a tap INSIDE YouTube's frame (phones refuse to start
  // a video from the page's own button), so the click shield waits for it.
  const [started, setStarted] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(1);
  const [full, setFull] = useState(false);
  const cb = useRef({ onWatched, onPlayingChange, onBlocked });
  cb.current = { onWatched, onPlayingChange, onBlocked };

  useEffect(() => {
    let player: YTPlayer = null;
    let timer: number | undefined;
    let fast: number | undefined;
    let cancelled = false;
    let last = -1;

    /** Belt and braces: YouTube re-loads captions on some state changes. */
    const killCaptions = () => {
      try { player?.unloadModule?.('captions'); player?.unloadModule?.('cc'); } catch { /* not loaded */ }
    };

    loadYouTubeApi().then(YT => {
      if (cancelled || !hostRef.current) return;
      const mount = document.createElement('div');
      hostRef.current.appendChild(mount);
      player = new YT.Player(mount, {
        videoId,
        width: '100%',
        height: '100%',
        playerVars: {
          controls: 0, disablekb: 1, cc_load_policy: 0, iv_load_policy: 3,
          fs: 0, rel: 0, modestbranding: 1, playsinline: 1,
        },
        events: {
          onReady: () => {
            playerRef.current = player;
            killCaptions();
            setDuration(player.getDuration?.() || 0);
            setReady(true);
          },
          onApiChange: killCaptions,
          onStateChange: (e: { data: number }) => {
            const isPlaying = e.data === YT.PlayerState.PLAYING;
            if (isPlaying) { killCaptions(); setStarted(true); }
            setPlaying(isPlaying);
            cb.current.onPlayingChange(isPlaying);
            if (!isPlaying) last = -1;
            const d = player.getDuration?.();
            if (d) setDuration(d);
          },
          onError: (e: { data: number }) => {
            if (e.data === 101 || e.data === 150) { cb.current.onBlocked?.(); return; }
            setError(
              e.data === 101 || e.data === 150
                ? (forTeacher
                  ? 'This video’s owner has turned off playback on other websites, so students can’t watch it here. Click Edit and pick a different video.'
                  : 'The owner of this video does not allow it to be played on other sites. Ask your teacher for another link.')
                : 'This video could not be played.',
            );
          },
        },
      });

      // Watch tracking, once a second.
      timer = window.setInterval(() => {
        if (!player?.getPlayerState || player.getPlayerState() !== YT.PlayerState.PLAYING) return;
        const t = player.getCurrentTime();
        const r = player.getPlaybackRate?.() || 1;
        if (last >= 0) {
          const d = t - last;
          // Faster than normal (2×, 1.5×…) doesn't count toward a watch.
          if (d > 0 && d < 2.5 * r && r <= 1) cb.current.onWatched(d, player.getDuration());
        }
        last = t;
      }, 1000);

      // The playhead for subtitles and the scrubber, four times a second.
      fast = window.setInterval(() => {
        if (player?.getCurrentTime) setTime(player.getCurrentTime() || 0);
      }, 250);
    }).catch(e => setError(e instanceof Error ? e.message : 'Could not load YouTube.'));

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.clearInterval(fast);
      playerRef.current = null;
      try { player?.destroy(); } catch { /* already gone */ }
      if (hostRef.current) hostRef.current.innerHTML = '';
    };
  }, [videoId, forTeacher]);

  useEffect(() => {
    const onFs = () => setFull(document.fullscreenElement === frameRef.current);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const p = () => playerRef.current;
  const toggle = () => { if (!p()) return; if (playing) p().pauseVideo(); else p().playVideo(); };
  const pause = useCallback(() => { p()?.pauseVideo?.(); }, []);
  const seek = (t: number) => { p()?.seekTo?.(Math.max(0, t), true); setTime(Math.max(0, t)); };
  const setSpeed = (r: number) => { p()?.setPlaybackRate?.(r); setRate(r); };
  const fullscreen = () => {
    const el = frameRef.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void el.requestFullscreen?.();
  };

  const list = cues ?? [];
  const idx = list.length ? cueIndexAt(list, time) : -1;
  const shown = idx >= 0 ? idx : lastCueBefore(list, time);
  // Hold the last line on screen through short gaps, dimmed, so a tap aimed at
  // a line that just ended still lands on it.
  const cue = shown >= 0 && time - list[shown].e < 2.5 ? list[shown] : null;

  const replayLine = () => {
    const i = lastCueBefore(list, time - 0.3);
    seek(i >= 0 ? list[i].s : Math.max(0, time - 5));
    p()?.playVideo?.();
  };

  return (
    <div className={`player${full ? ' full' : ''}`} ref={frameRef}>
      <div className="video-frame">
        <div ref={hostRef} className="video-host" />
        {/* Clicks on the picture play/pause through our handler, so YouTube's
            own overlay menus never open. */}
        {started && !error && <button className="video-shield" aria-label={playing ? 'Pause' : 'Play'} onClick={toggle} />}
        {error && <div className="notice bad video-error">{error}</div>}
      </div>

      {list.length > 0 && (
        <div
          className={`subtitle-bar${cue && idx < 0 ? ' between' : ''}`}
          onPointerDown={pause}
        >
          {cue
            ? <TranslatableText key={`${cue.s}`} text={cue.t} source={source} target={target}
                onLookup={onLookup} onSave={onSave} savedKeys={savedKeys} className="subtitle" />
            : <span className="subtitle-empty">…</span>}
        </div>
      )}

      <div className="controls">
        <button className="ctl" onClick={toggle} disabled={!ready} aria-label={playing ? 'Pause' : 'Play'}>
          {playing ? '❚❚' : '▶'}
        </button>
        <button className="ctl" onClick={replayLine} disabled={!ready} title="Replay the last line" aria-label="Replay the last line">⟲</button>
        <span className="ctl-time">{clock(time)}</span>
        <input
          className="scrub" type="range" min={0} max={duration || 0} step={0.5} value={Math.min(time, duration || 0)}
          disabled={!ready || !duration}
          onChange={e => seek(Number(e.target.value))}
          aria-label="Position"
        />
        <span className="ctl-time">{clock(duration)}</span>
        <select className="ctl-speed" value={rate} onChange={e => setSpeed(Number(e.target.value))} disabled={!ready} aria-label="Speed">
          {SPEEDS.map(r => <option key={r} value={r}>{r}×</option>)}
        </select>
        {document.fullscreenEnabled && (
          <button className="ctl" onClick={fullscreen} aria-label={full ? 'Exit full screen' : 'Full screen'}>{full ? '⤡' : '⤢'}</button>
        )}
      </div>
    </div>
  );
}
